<div class="flex gap-6 py-4">
  <!-- Parameters list -->
  <div class="w-1/3 min-w-[18rem]">
    <div class="flex items-center justify-between mb-3">
      <h2 class="text-lg font-semibold">Paramètres</h2>
      <button
        pButton
        type="button"
        icon="pi pi-plus"
        label="Nouveau paramètre"
        size="small"
        (click)="openCreateParameter()"
      ></button>
    </div>

    <div class="flex items-center gap-2 mb-3">
      <p-toggleswitch
        inputId="show-deleted-gain"
        [ngModel]="showDeleted"
        (onChange)="onShowDeletedChange($event.checked)"
      ></p-toggleswitch>
      <label for="show-deleted-gain" class="text-sm"
        >Afficher les éléments supprimés</label
      >
    </div>

    <div *ngIf="loading" class="flex justify-center p-4">
      <p-progressSpinner
        [style]="{ width: '40px', height: '40px' }"
      ></p-progressSpinner>
    </div>

    <p *ngIf="!loading && parameters.length === 0" class="text-gray-500 p-2">
      Aucun paramètre défini.
    </p>

    <div class="flex flex-col gap-2">
      <div
        *ngFor="let parameter of parameters"
        class="flex items-center justify-between p-3 border rounded cursor-pointer border-gray-200 dark:border-gray-700"
        [ngClass]="{
          'bg-blue-50 border-blue-400': parameter.uid === selectedUid,
          'opacity-60 bg-gray-100': parameter.isDeleted,
        }"
        (click)="selectParameter(parameter)"
      >
        <div class="flex flex-col min-w-0">
          <span
            class="font-medium truncate"
            [ngClass]="{ 'line-through': parameter.isDeleted }"
            >{{ parameter.name }}</span
          >
          <span
            *ngIf="parameter.description"
            class="text-xs text-gray-500 truncate"
            >{{ parameter.description }}</span
          >
          <div class="flex gap-1 mt-1">
            <p-tag [value]="typeLabel(parameter.type)" severity="info"></p-tag>
            <p-tag
              *ngIf="parameter.isDeleted"
              value="Supprimé"
              severity="danger"
            ></p-tag>
          </div>
        </div>
        <div *ngIf="!parameter.isDeleted" class="flex gap-1">
          <button
            pButton
            type="button"
            icon="pi pi-pencil"
            class="p-button-text p-button-sm"
            [disabled]="saving"
            (click)="$event.stopPropagation(); openEditParameter(parameter)"
          ></button>
          <button
            pButton
            type="button"
            icon="pi pi-trash"
            class="p-button-text p-button-danger p-button-sm"
            [disabled]="saving"
            (click)="
              $event.stopPropagation(); confirmDeleteParameter(parameter)
            "
          ></button>
        </div>
      </div>
    </div>
  </div>

  <!-- Values -->
  <div class="flex-1 min-w-0">
    <ng-container *ngIf="selectedParameter as parameter; else noSelection">
      <p-message
        *ngIf="parameter.type === 'Decimal'"
        severity="info"
        text="Les paramètres décimaux n'ont pas de valeurs prédéfinies : la valeur est saisie lors de l'ajout de la formule à une règle."
      ></p-message>
      <ng-container *ngIf="parameter.type !== 'Decimal'">
      <div class="flex items-center justify-between mb-3">
        <h2 class="text-lg font-semibold">
          Valeurs de « {{ parameter.name }} »
        </h2>
        <button
          pButton
          type="button"
          icon="pi pi-plus"
          label="Nouvelle valeur"
          size="small"
          [disabled]="parameter.isDeleted"
          (click)="openCreateValue()"
        ></button>
      </div>

      <div class="flex flex-wrap items-center gap-3 mb-3">
        <p-select
          inputId="gain-filter-plant"
          placeholder="Filtrer par usine"
          [options]="factoryFilterOptions"
          [ngModel]="factoryFilter"
          (ngModelChange)="onFactoryFilterChange($event)"
          [filter]="true"
          filterBy="label"
          [showClear]="true"
          appendTo="body"
          [style]="{ 'min-width': '14rem' }"
        ></p-select>
        <p-select
          inputId="gain-filter-label"
          placeholder="Filtrer par étiquette"
          [options]="labelFilterOptions"
          [ngModel]="labelFilter"
          (ngModelChange)="onLabelFilterChange($event)"
          [filter]="true"
          filterBy="label"
          [showClear]="true"
          appendTo="body"
          [style]="{ 'min-width': '14rem' }"
        ></p-select>
      </div>

      <p-table
        [value]="filteredValues"
        [tableStyle]="{ 'min-width': '30rem' }"
      >
        <ng-template #header>
          <tr>
            <th>Étiquette</th>
            <th>Valeur</th>
            <th>Usine</th>
            <th style="width: 8rem"></th>
          </tr>
        </ng-template>
        <ng-template #body let-value>
          <tr [ngClass]="{ 'opacity-60 bg-gray-100': value.isDeleted }">
            <td [ngClass]="{ 'line-through': value.isDeleted }">
              {{ value.label }}
              <p-tag
                *ngIf="value.isDeleted"
                class="ml-2"
                value="Supprimé"
                severity="danger"
              ></p-tag>
            </td>
            <td [ngClass]="{ 'line-through': value.isDeleted }">
              {{ value.value }}
            </td>
            <td>{{ plantLabel(value) }}</td>
            <td>
              <div
                *ngIf="!value.isDeleted && !parameter.isDeleted"
                class="flex gap-1"
              >
                <button
                  pButton
                  type="button"
                  icon="pi pi-pencil"
                  class="p-button-text p-button-sm"
                  [disabled]="saving"
                  (click)="openEditValue(value)"
                ></button>
                <button
                  pButton
                  type="button"
                  icon="pi pi-trash"
                  class="p-button-text p-button-danger p-button-sm"
                  [disabled]="saving"
                  (click)="confirmDeleteValue(value)"
                ></button>
              </div>
            </td>
          </tr>
        </ng-template>
        <ng-template #emptymessage>
          <tr>
            <td colspan="4" class="text-center text-gray-500">
              {{
                parameter.values.length
                  ? 'Aucune valeur ne correspond aux filtres.'
                  : 'Aucune valeur pour ce paramètre.'
              }}
            </td>
          </tr>
        </ng-template>
      </p-table>
      </ng-container>
    </ng-container>

    <ng-template #noSelection>
      <p class="text-gray-500 p-4">
        Sélectionnez un paramètre pour gérer ses valeurs.
      </p>
    </ng-template>
  </div>
</div>

<!-- FeaturesModule provides its own ConfirmationService, so the root dialog never receives it. -->
<p-confirmDialog></p-confirmDialog>

<!-- Parameter dialog -->
<p-dialog
  [header]="editingParameter ? 'Modifier le paramètre' : 'Nouveau paramètre'"
  [(visible)]="parameterDialogVisible"
  [modal]="true"
  [style]="{ width: '30rem' }"
>
  <div class="flex flex-col gap-4">
    <div class="flex flex-col gap-1">
      <label for="gain-param-name">Nom</label>
      <input
        id="gain-param-name"
        type="text"
        pInputText
        maxlength="255"
        [(ngModel)]="parameterForm.name"
      />
    </div>
    <div class="flex flex-col gap-1">
      <label for="gain-param-description">Description</label>
      <input
        id="gain-param-description"
        type="text"
        pInputText
        maxlength="1000"
        [(ngModel)]="parameterForm.description"
      />
    </div>
    <div class="flex flex-col gap-1">
      <label for="gain-param-type">Type</label>
      <p-select
        inputId="gain-param-type"
        [options]="typeOptions"
        [(ngModel)]="parameterForm.type"
        [disabled]="!!editingParameter"
        appendTo="body"
      ></p-select>
    </div>
  </div>
  <ng-template #footer>
    <button
      pButton
      type="button"
      label="Annuler"
      class="p-button-text"
      (click)="parameterDialogVisible = false"
    ></button>
    <button
      pButton
      type="button"
      label="Enregistrer"
      [disabled]="!canSaveParameter"
      (click)="saveParameter()"
    ></button>
  </ng-template>
</p-dialog>

<!-- Value dialog -->
<p-dialog
  [header]="editingValue ? 'Modifier la valeur' : 'Nouvelle valeur'"
  [(visible)]="valueDialogVisible"
  [modal]="true"
  [style]="{ width: '30rem' }"
>
  <div class="flex flex-col gap-4">
    <div class="flex flex-col gap-1">
      <label for="gain-value-label">Étiquette</label>
      <input
        id="gain-value-label"
        type="text"
        pInputText
        maxlength="255"
        [(ngModel)]="valueForm.label"
      />
    </div>
    <div class="flex flex-col gap-1">
      <label for="gain-value-value">Valeur</label>
      <p-inputnumber
        inputId="gain-value-value"
        [(ngModel)]="valueForm.value"
        mode="decimal"
        [minFractionDigits]="0"
        [maxFractionDigits]="6"
        [useGrouping]="false"
      ></p-inputnumber>
    </div>
    <div class="flex flex-col gap-1">
      <label for="gain-value-plant">Usine</label>
      <p-select
        inputId="gain-value-plant"
        [options]="plantOptions"
        [(ngModel)]="valueForm.factoryFK"
        [filter]="true"
        filterBy="label"
        appendTo="body"
      ></p-select>
    </div>
  </div>
  <ng-template #footer>
    <button
      pButton
      type="button"
      label="Annuler"
      class="p-button-text"
      (click)="valueDialogVisible = false"
    ></button>
    <button
      pButton
      type="button"
      label="Enregistrer"
      [disabled]="!canSaveValue"
      (click)="saveValue()"
    ></button>
  </ng-template>
</p-dialog>
